import React, { useState } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { authAPI } from '../utils/api';
import { validatePassword, passwordErrorMessage } from '../utils/passwordValidation';

const AccountSettings: React.FC = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'accountSettings' });
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passError, setPassError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  const [showPass, setShowPass] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'new_password' && passError) {
      const validation = validatePassword(value);
      setPassError(validation.valid ? '' : (validation.error || ''));
    }

    if (name === 'new_password_confirmation' && confirmError) {
      setConfirmError(value === formData.new_password ? '' : t('passwordsDoNotMatch'));
    }

    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validation = validatePassword(formData.new_password);
    if (!validation.valid) {
      setPassError(validation.error || '');
      return;
    }

    if (formData.new_password !== formData.new_password_confirmation) {
      setConfirmError(t('passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.changePassword(formData);
      setSuccess(response.message || t('passwordUpdated'));
      setFormData({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('passwordChangeError'));
    } finally {
      setLoading(false);
    }
  };

  const revealButton = (field: keyof typeof showPass) => (
    <Button
      variant="outline-secondary"
      className="fw-semibold px-3"
      type="button"
      onClick={() => setShowPass(prev => ({ ...prev, [field]: !prev[field] }))}
    >
      {showPass[field] ? t('hide') : t('show')}
    </Button>
  );

  return (
    <Layout>
      <Container fluid className="an-enterprise-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath="/settings"
        />

        <Row className="justify-content-center mt-4">
          <Col xxl={6} xl={7} lg={8} md={10}>
            <Card className="border-0 shadow-sm an-panel overflow-hidden">
              <Card.Header className="bg-white border-bottom px-4 py-3 an-section-divider">
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <div>
                    <h5 className="mb-1 fw-bold text-dark">{t('changePassword')}</h5>
                    <p className="mb-0 small text-muted">{passwordErrorMessage}</p>
                  </div>
                </div>
              </Card.Header>

              <Card.Body className="p-4">
                {error && <Alert variant="danger" className="mb-4 fw-semibold">{error}</Alert>}
                {success && <Alert variant="success" className="mb-4 fw-semibold">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold text-uppercase text-muted mb-2">
                      {t('currentPassword')}
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPass.current ? 'text' : 'password'}
                        name="current_password"
                        value={formData.current_password}
                        onChange={handleInputChange}
                        required
                        placeholder={t('currentPasswordPlaceholder')}
                      />
                      {revealButton('current')}
                    </InputGroup>
                  </Form.Group>

                  <div className="border-top pt-4 mt-4">
                    <Row className="g-4">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small fw-bold text-uppercase text-muted mb-2">
                            {t('newPassword')}
                          </Form.Label>
                          <InputGroup hasValidation>
                            <Form.Control
                              type={showPass.new ? 'text' : 'password'}
                              name="new_password"
                              value={formData.new_password}
                              onChange={handleInputChange}
                              isInvalid={!!passError}
                              required
                              placeholder={t('newPasswordPlaceholder')}
                            />
                            {revealButton('new')}
                            <Form.Control.Feedback type="invalid">
                              {passError}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small fw-bold text-uppercase text-muted mb-2">
                            {t('confirmNewPassword')}
                          </Form.Label>
                          <InputGroup hasValidation>
                            <Form.Control
                              type={showPass.confirm ? 'text' : 'password'}
                              name="new_password_confirmation"
                              value={formData.new_password_confirmation}
                              onChange={handleInputChange}
                              isInvalid={!!confirmError}
                              required
                              placeholder={t('confirmPasswordPlaceholder')}
                            />
                            {revealButton('confirm')}
                            <Form.Control.Feedback type="invalid">
                              {confirmError}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  <div className="d-flex justify-content-end border-top pt-4 mt-4">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading || !!passError || !!confirmError}
                      className="px-4 py-2 fw-bold"
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          {t('updating')}
                        </>
                      ) : (
                        t('updatePassword')
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default AccountSettings;
